import { useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useUiStore } from '@/features/settings/ui-store.js';
import { onAppBackground } from '@/lib/app-presence.js';
import { saveLastLocation, type LastLocation } from '@/lib/api/preferences.js';
import { logger } from '@/logger.js';

import { restorableHref } from './restorable-location.js';
import { wasStartupFallback } from './startup-location.js';

/** A run of quick navigations is stored once, where it ends. */
const RECORD_DEBOUNCE_MS = 1_000;

let pending: LastLocation | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let lastRecordedKey: string | null = null;
let seenFirstLocation = false;
let stopBackgroundFlush: (() => void) | null = null;

/**
 * Remembers, in the user's preferences, the page they are on, so the app can reopen it after a Teams
 * tab reload or on another device. Only pages worth reopening are recorded (see
 * {@link restorableHref}); a Home the start-up merely fell back to is not, because the user did not
 * choose it. A waiting record is sent at once, with keepalive, when the app goes to the background.
 */
export function useLastLocationRecorder(enabled: boolean): void {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const searchStr = useRouterState({
    select: (state) => state.location.searchStr,
  });
  const workspaceId = useUiStore((state) => state.selectedWorkspaceId);

  useEffect(() => {
    if (!enabled) return;
    stopBackgroundFlush ??= onAppBackground(() => sendPendingLocation(true));
    const firstLocation = !seenFirstLocation;
    seenFirstLocation = true;
    if (firstLocation && wasStartupFallback()) return;
    const href = restorableHref(pathname, searchStr);
    if (href === null) return;
    scheduleLocation({ href, workspaceId, at: new Date().toISOString() });
  }, [enabled, pathname, searchStr, workspaceId]);
}

/** Queues `location` to be stored after the debounce, unless it is the page already recorded. */
function scheduleLocation(location: LastLocation): void {
  const key = `${location.workspaceId ?? ''} ${location.href}`;
  if (key === lastRecordedKey) return;
  lastRecordedKey = key;
  pending = location;
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => void sendPendingLocation(false), RECORD_DEBOUNCE_MS);
}

/**
 * Sends the waiting page. A failed send stays waiting, unless a newer page took its place, so the
 * next background flush tries it again instead of losing it for the rest of the tab's life.
 */
async function sendPendingLocation(keepalive: boolean): Promise<void> {
  if (timer !== null) clearTimeout(timer);
  timer = null;
  const location = pending;
  pending = null;
  if (location === null) return;
  try {
    await saveLastLocation(location, { keepalive });
  } catch (error: unknown) {
    pending ??= location;
    logger.debug('last-location: could not store the page', error);
  }
}

/** Test-only: forgets everything recorded and stops listening for the background. */
export function _resetLastLocationRecorderForTests(): void {
  if (timer !== null) clearTimeout(timer);
  timer = null;
  pending = null;
  lastRecordedKey = null;
  seenFirstLocation = false;
  stopBackgroundFlush?.();
  stopBackgroundFlush = null;
}
